<template>
  <div class="tab-manager-container flex flex-col flex-1">
    <!-- 标签页导航栏 -->
    <div class="tab-manager bg-white border-b border-gray-200 relative w-full z-40">
      <div class="flex items-center">
        <!-- 左翻页按钮 -->
        <button 
          v-show="showLeftArrow"
          @click="scrollLeft"
          class="p-2 hover:bg-gray-100 text-gray-500 hover:text-gray-700 flex-shrink-0 border-r border-gray-200"
          title="向左滚动"
        >
          <i class="fas fa-chevron-left text-sm"></i>
        </button>
        
        <!-- 标签页管理容器 -->
        <div 
          ref="tabsContainer"
          class="flex items-center overflow-x-hidden scrollbar-hide flex-1 min-w-0 scroll-smooth"
          @scroll="handleScroll"
        >
          <!-- 标签页管理 -->
          <div 
            v-for="tab in tabs" 
            :key="tab.path"
            :ref="el => setTabRef(el, tab.path)"
            class="flex items-center min-w-0 border-r border-gray-200 group relative flex-shrink-0"
            :class="{
              'bg-blue-50 border-b-2 border-b-blue-500': tab.path === activeTabPath,
              'hover:bg-gray-50': tab.path !== activeTabPath
            }"
          >
            <!-- 刷新按钮 -->
            <button 
              @click.stop="refreshTab(tab)"
              class="p-1 ml-1 opacity-0 group-hover:opacity-100 hover:bg-gray-200 rounded transition-opacity flex-shrink-0"
              :class="{
                'opacity-100': tab.path === activeTabPath
              }"
              title="刷新页面"
            >
              <i class="fas fa-sync-alt text-xs text-gray-500 hover:text-gray-700"></i>
            </button>
            
            <!-- 标签页内容 -->
            <div 
              @click="switchTab(tab)"
              class="flex items-center px-4 py-2 cursor-pointer min-w-0 max-w-48"
            >
              <!-- <i v-if="tab.icon" :class="tab.icon" class="mr-2 text-sm flex-shrink-0"></i> -->
              <span class="text-sm truncate" :title="tab.title">{{ tab.title }}</span>
            </div>
            
            <!-- 关闭按钮 -->
            <button 
              v-if="tabs.length > 1"
              @click.stop="closeTab(tab)"
              class="p-1 mr-2 opacity-0 group-hover:opacity-100 hover:bg-gray-200 rounded transition-opacity flex-shrink-0"
              :class="{
                'opacity-100': tab.path === activeTabPath
              }"
            >
              <i class="fas fa-times text-xs text-gray-500 hover:text-gray-700"></i>
            </button>
          </div>
        </div>
        
        <!-- 右翻页按钮 -->
        <button 
          v-show="showRightArrow"
          @click="scrollRight"
          class="p-2 hover:bg-gray-100 text-gray-500 hover:text-gray-700 flex-shrink-0 border-l border-gray-200"
          title="向右滚动"
        >
          <i class="fas fa-chevron-right text-sm"></i>
        </button>
        
        <!-- 右侧操作按钮 - 固定在右侧 -->
        <div class="flex items-center border-l border-gray-200 flex-shrink-0">
          <!-- 关闭其他标签 -->
          <button 
            v-if="tabs.length > 1"
            @click="closeOtherTabs"
            class="p-2 hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            title="关闭其他标签"
          >
            <i class="fas fa-times-circle text-sm"></i>
          </button>
          
          <!-- 关闭所有标签 -->
          <button 
            v-if="tabs.length > 1"
            @click="closeAllTabs"
            class="p-2 hover:bg-gray-100 text-gray-500 hover:text-gray-700"
            title="关闭所有标签"
          >
            <i class="fas fa-times text-sm"></i>
          </button>
        </div>
      </div>
    </div>
    
    <!-- 内容区域 -->
    <div class="bg-white flex-1 px-1 py-2 overflow-y-auto">
      <div 
        v-for="tab in tabs" 
        :key="tab.path"
        v-show="tab.path === activeTabPath"
        class="pt-0"
      >
        <component 
          :is="componentMap[tab.path]" 
          v-if="componentMap[tab.path]"
          :key="`${tab.path}-${getComponentKey(tab.path)}`"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, watch, defineAsyncComponent, nextTick, onMounted, onUnmounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const route = useRoute()
const router = useRouter()

// 常量配置
const CONSTANTS = {
  DEFAULT_ROUTE: '/dashboard',
  SCROLL_AMOUNT: 200,
  DEBOUNCE_DELAY: 100,
  MAX_TAB_WIDTH: 'max-w-48'
}

// 标签页数据
const tabs = ref([])

// 当前激活的标签页路径
const activeTabPath = ref(CONSTANTS.DEFAULT_ROUTE)

// 标签页容器引用
const tabsContainer = ref(null)

// 标签页元素引用映射
const tabRefs = ref({})

// 翻页按钮显示状态
const showLeftArrow = ref(false)
const showRightArrow = ref(false)

// 组件映射
const componentMap = {
  '/login': defineAsyncComponent(() => import('@/views/Formlogin/FormLogin.vue')),
  '/dashboard': defineAsyncComponent(() => import('@/views/MainDashboard.vue')),
  '/messagelist': defineAsyncComponent(() => import('@/views/settings/MessageList.vue')),
  '/messageDetail': defineAsyncComponent(() => import('@/views/settings/MessageDetail.vue')),
  // 销售管理
  '/Sales/Customers': defineAsyncComponent(() => import('@/views/Sales/CustomerList.vue')),
  '/Sales/CustomerAdd': defineAsyncComponent(() => import('@/views/Sales/CustomerAdd.vue')),
  '/Sales/SalesAdd': defineAsyncComponent(() => import('@/views/Sales/SalesAdd.vue')),
  '/Sales/SalesListOrder': defineAsyncComponent(() => import('@/views/Sales/SalesListOrder.vue')),
  '/Sales/SalesReturnList': defineAsyncComponent(() => import('@/views/Sales/SalesReturnList.vue')),
  '/Sales/SalesReturnAdd': defineAsyncComponent(() => import('@/views/Sales/SalesReturnAdd.vue')),
  '/Sales/CustomerCategoryManage': defineAsyncComponent(() => import('@/views/Sales/CustomerCategoryManage.vue')),
  '/Sales/SalesDetailList': defineAsyncComponent(() => import('@/views/Sales/SalesDetailList.vue')),
  // 销售报表
  '/Reports/SalesReport': defineAsyncComponent(() => import('@/views/Reports/SalesReport.vue')),
'/Reports/CustomerReport': defineAsyncComponent(() => import('@/views/Reports/CustomerReport.vue')), 
  '/CustomerPrice/CustomerPriceIndex': defineAsyncComponent(() => import('@/views/CustomerPrice/CustomerPriceIndex.vue')),
  '/CustomerPrice/CustomerPriceList': defineAsyncComponent(() => import('@/views/CustomerPrice/CustomerPriceList.vue')),
  '/CustomerPrice/CustomerPriceAdd': defineAsyncComponent(() => import('@/views/CustomerPrice/CustomerPriceAdd.vue')),
  '/CustomerPrice/CustomerPriceHistory': defineAsyncComponent(() => import('@/views/CustomerPrice/CustomerPriceHistory.vue')),
  '/CustomerPrice/CustomerPriceCalculator': defineAsyncComponent(() => import('@/views/CustomerPrice/CustomerPriceCalculator.vue')),
  // 库存管理
  '/inventory/InventoryCheckList': defineAsyncComponent(() => import('@/views/Inventory/InventoryCheckList.vue')),
  '/inventory/InventoryCheckAdd': defineAsyncComponent(() => import('@/views/Inventory/InventoryCheckAdd.vue')),
  '/inventory/AssemblyAdd': defineAsyncComponent(() => import('@/views/Inventory/AssemblyAdd.vue')),
  '/inventory/AssemblyList': defineAsyncComponent(() => import('@/views/Inventory/AssemblyList.vue')),
  '/inventory/OtherTransferAdd': defineAsyncComponent(() => import('@/views/Inventory/OtherTransferAdd.vue')),
  '/inventory/OtherTransferList': defineAsyncComponent(() => import('@/views/Inventory/OtherTransferList.vue')),
  // 兼容性组件映射
  '/inventory/OtherInboundAdd': defineAsyncComponent(() => import('@/views/Inventory/OtherTransferAdd.vue')),
  '/inventory/OtherInboundList': defineAsyncComponent(() => import('@/views/Inventory/OtherTransferList.vue')),
  '/inventory/OtherOutboundAdd': defineAsyncComponent(() => import('@/views/Inventory/OtherTransferAdd.vue')),
  '/inventory/OtherOutboundList': defineAsyncComponent(() => import('@/views/Inventory/OtherTransferList.vue')),
  '/inventory/InventoryList': defineAsyncComponent(() => import('@/views/Inventory/InventoryList.vue')),
  '/inventory/in': defineAsyncComponent(() => import('@/views/Inventory/StockIn.vue')),
  '/inventory/out': defineAsyncComponent(() => import('@/views/Inventory/StockOut.vue')),
  // 财务管理
  '/Finance/BankAccountList': defineAsyncComponent(() => import('@/views/Finance/BankAccountList.vue')),
  '/Finance/BankAccountAdd': defineAsyncComponent(() => import('@/views/Finance/BankAccountAdd.vue')),
  '/Finance/IncomeExpenseList': defineAsyncComponent(() => import('@/views/Finance/IncomeExpenseList.vue')),
  '/Finance/IncomeExpenseAdd': defineAsyncComponent(() => import('@/views/Finance/IncomeExpenseAdd.vue')),
  '/Finance/IncomeExpenseTypeList': defineAsyncComponent(() => import('@/views/Finance/IncomeExpenseTypeList.vue')),
  '/Finance/ReceivableList': defineAsyncComponent(() => import('@/views/Finance/ReceivableList.vue')),
  '/Finance/ReceiptList': defineAsyncComponent(() => import('@/views/Finance/ReceiptList.vue')),
  '/Finance/ReceiptAdd': defineAsyncComponent(() => import('@/views/Finance/ReceiptAdd.vue')),
  '/Finance/PayableList': defineAsyncComponent(() => import('@/views/Finance/PayableList.vue')),
  '/Finance/PaymentList': defineAsyncComponent(() => import('@/views/Finance/PaymentList.vue')),
  '/Finance/PaymentAdd': defineAsyncComponent(() => import('@/views/Finance/PaymentAdd.vue')),
  '/Finance/SalesInvoiceList': defineAsyncComponent(() => import('@/views/Finance/SalesInvoiceList.vue')),
  '/Finance/WriteOffVoucherList': defineAsyncComponent(() => import('@/views/Finance/WriteOffVoucherList.vue')),
  '/Finance/WriteOffVoucherCreate': defineAsyncComponent(() => import('@/views/Finance/WriteOffVoucherForm.vue')),
  '/Finance/WriteOffVoucherEdit': defineAsyncComponent(() => import('@/views/Finance/WriteOffVoucherForm.vue')),
  '/Finance/WriteOffVoucherDetail': defineAsyncComponent(() => import('@/views/Finance/WriteOffVoucherDetail.vue')),
  '/finance/invoice': defineAsyncComponent(() => import('@/views/Finance/InvoiceList.vue')),
  '/Finance/InvoiceDetail': defineAsyncComponent(() => import('@/views/Finance/InvoiceDetail.vue')),
  // '/Finance/InvoiceApplicationListForSales': defineAsyncComponent(() => import('@/views/Finance/InvoiceApplicationListForSales.vue')),
  '/Finance/InvoiceApplicationList': defineAsyncComponent(() => import('@/views/Finance/InvoiceApplicationList.vue')),
  '/Finance/InvoiceApplicationDetail': defineAsyncComponent(() => import('@/views/Finance/InvoiceApplicationDetail.vue')),
  '/finance/payment': defineAsyncComponent(() => import('@/views/Finance/PaymentProcess.vue')),
  '/finance/fund-flows': defineAsyncComponent(() => import('@/views/Finance/FinanceReports.vue')),
  '/reports/employee-commission':defineAsyncComponent(()=>import('@/views/Reports/EmployeeCommission/EmployeeReport.vue')), 

  // 进货管理
  '/Purchase/PurchaseAdd': defineAsyncComponent(() => import('@/views/Purchase/PurchaseAdd.vue')),
  '/Purchase/PurchaseListOrder': defineAsyncComponent(() => import('@/views/Purchase/PurchaseListOrder.vue')),
  '/Purchase/PurchaseReturnAdd': defineAsyncComponent(() => import('@/views/Purchase/PurchaseReturnAdd.vue')),
  '/Purchase/PurchaseReturnList': defineAsyncComponent(() => import('@/views/Purchase/PurchaseReturnList.vue')),
  '/Purchase/SupplierAdd': defineAsyncComponent(() => import('@/views/Purchase/SupplierAdd.vue')),
  '/Purchase/SupplierList': defineAsyncComponent(() => import('@/views/Purchase/SupplierList.vue')),
  '/Purchase/PurchaseReport': defineAsyncComponent(() => import('@/views/Purchase/PurchaseReport.vue')),
  '/Purchase/SupplierCategoryManage': defineAsyncComponent(() => import('@/views/Purchase/SupplierCategoryManage.vue')),
  '/Purchase/PurchaseDetailList': defineAsyncComponent(() => import('@/views/Purchase/PurchaseDetailList.vue')),
  // 系统设置
  '/system/config': defineAsyncComponent(() => import('@/views/System/ConfigManager.vue')),
  '/settings/UserManagement': defineAsyncComponent(() => import('@/views/settings/UserManagement.vue')),
  '/settings/ChannelLevelConfigManagement': defineAsyncComponent(() => import('@/views/settings/ChannelLevelConfigManagement.vue')),
  '/settings/ChannelMerchantManagement': defineAsyncComponent(() => import('@/views/settings/ChannelMerchantManagement.vue')),
  '/settings/salesman': defineAsyncComponent(() => import('@/views/settings/SalesmanList.vue')),
  '/settings/salesman/add': defineAsyncComponent(() => import('@/views/settings/SalesmanAdd.vue')),
  '/settings/salesman/edit': defineAsyncComponent(() => import('@/views/settings/SalesmanAdd.vue')),
  '/settings/roles': defineAsyncComponent(() => import('@/views/settings/UserRoles.vue')),
  '/settings/system': defineAsyncComponent(() => import('@/views/settings/SystemSettings.vue')),
  '/settings/message-config': defineAsyncComponent(() => import('@/views/settings/MessageConfig.vue')),
  '/settings/ProductCreate': defineAsyncComponent(() => import('@/views/settings/ProductCreate.vue')),
  '/settings/data-authorization': defineAsyncComponent(() => import('@/views/settings/DataAuthorization.vue')),
  '/platform/companies': defineAsyncComponent(() => import('@/views/platform/PlatformCompanyManagement.vue')),
  '/settings/ProductManagement': defineAsyncComponent(() => import('@/views/settings/ProductManagement.vue')),
  '/settings/CategoryManagement': defineAsyncComponent(() => import('@/views/settings/CategoryManagement.vue')),
  '/settings/approval-workflows': defineAsyncComponent(() => import('@/views/settings/ApprovalWorkflowList.vue')),
  '/ShopOrders/OrderList': defineAsyncComponent(() => import('@/views/ShopOrders/OrderList.vue')),
  '/settings/warehouse': defineAsyncComponent(() => import('@/views/settings/WarehouseList.vue')),
  // 审批管理
  '/approval/my-pending': defineAsyncComponent(() => import('@/views/approval/MyPendingApprovals.vue')),
  '/approval/my-completed': defineAsyncComponent(() => import('@/views/approval/MyCompletedApprovals.vue')),
  '/approval/my-applications': defineAsyncComponent(() => import('@/views/approval/MyApplications.vue')),
  '/approval/payment-management': defineAsyncComponent(() => import('@/views/ApprovalV2/PaymentApprovalManagement.vue')),
  '/approval-v2/payment': defineAsyncComponent(() => import('@/views/ApprovalV2/PaymentApprovalManagement.vue')),
  '/approval-v2/payment/add': defineAsyncComponent(() => import('@/views/ApprovalV2/PaymentAdd.vue')),
  '/approval/invoice-management': defineAsyncComponent(() => import('@/views/ApprovalV2/InvoiceApprovalManagement.vue')),
  '/approval-v2/invoice': defineAsyncComponent(() => import('@/views/ApprovalV2/InvoiceApprovalManagement.vue')),
  '/approval-v2/invoice/add':defineAsyncComponent(() => import('@/views/ApprovalV2/InvoiceAdd.vue')),
  '/approval/seal-management': defineAsyncComponent(() => import('@/views/ApprovalV2/SealApprovalManagement.vue')),
  '/approval-v2/seal/add': defineAsyncComponent(() => import('@/views/ApprovalV2/SealAdd.vue')),
  '/approval/history': defineAsyncComponent(() => import('@/views/approval/ApprovalHistory.vue')),
  '/approval/email-action': defineAsyncComponent(() => import('@/views/approval/EmailApprovalAction.vue')),
  '/approval/email-template-config': defineAsyncComponent(() => import('@/views/approval/EmailTemplateConfig.vue')),
  '/approval-v2/payment/edit': defineAsyncComponent(() => import('@/views/ApprovalV2/PaymentEdit.vue')),
  '/approval-v2/invoice/edit': defineAsyncComponent(() => import('@/views/ApprovalV2/InvoiceEdit.vue')),
  '/approval-v2/seal/edit': defineAsyncComponent(() => import('@/views/ApprovalV2/SealEdit.vue')),
  //报表
  '/Reports/CompanyFinanceReport': defineAsyncComponent(() => import('@/views/Reports/CompanyFinanceReport.vue')), 
}

// // 当前激活的组件
// const activeComponent = computed(() => {
//   return componentMap[activeTabPath.value] || null
// })

// 路由标题映射
const routeTitleMap = {
  '/dashboard': { title: '仪表盘', icon: 'fas fa-chart-line' },
  // 消息
  '/messagelist': { title: '消息管理', icon: 'fas fa-envelope' },
  '/messageDetail': { title: '消息详情', icon: 'fas fa-envelope-open' },
  // 销售管理
  '/Sales/Orders': { title: '订单管理', icon: 'fas fa-shopping-cart' },
  '/Sales/Customers': { title: '客户管理', icon: 'fas fa-users' },
  '/Sales/CustomerAdd': { title: '新增客户', icon: 'fas fa-user-plus' },
  '/Sales/SalesAdd': { title: '新增销售', icon: 'fas fa-plus-circle' },
  '/Sales/SalesListOrder': { title: '销售单管理', icon: 'fas fa-cash-register' },
  '/Sales/SalesReturnList': { title: '销售退货单管理', icon: 'fas fa-undo' },
  '/Sales/SalesReturnAdd': { title: '新增销售退货', icon: 'fas fa-undo-alt' },
  '/Sales/SalesDetailList': { title: '销售订单明细列表', icon: 'fas fa-list' },
  '/Sales/CustomerCategoryManage': { title: '客户分类管理', icon: 'fas fa-tags' },
  '/CustomerPrice/CustomerPriceIndex': { title: '一客一价管理', icon: 'fas fa-tags' },
  '/CustomerPrice/CustomerPriceList': { title: '客户价格列表', icon: 'fas fa-list' },
  '/CustomerPrice/CustomerPriceAdd': { title: '新增客户价格', icon: 'fas fa-plus' },
  '/CustomerPrice/CustomerPriceHistory': { title: '价格历史记录', icon: 'fas fa-history' },
  '/CustomerPrice/CustomerPriceCalculator': { title: '价格计算器', icon: 'fas fa-calculator' },
  // 进货管理
  '/Purchase/PurchaseAdd': { title: '新增进货', icon: 'fas fa-shopping-bag' },
  '/Purchase/PurchaseListOrder': { title: '进货单管理', icon: 'fas fa-shopping-cart' },
  '/Purchase/PurchaseReturnAdd': { title: '新增进货退货', icon: 'fas fa-undo' },
  '/Purchase/PurchaseReturnList': { title: '进货退货单管理', icon: 'fas fa-list' },
  '/Purchase/SupplierAdd': { title: '新增供应商', icon: 'fas fa-plus' },
  '/Purchase/SupplierList': { title: '供应商管理', icon: 'fas fa-truck' },
  '/Purchase/PurchaseReport': { title: '进货报表', icon: 'fas fa-chart-line' },
  '/Purchase/SupplierCategoryManage': { title: '供应商分类管理', icon: 'fas fa-tags' },
  '/Purchase/PurchaseDetailList': { title: '进货订单明细列表', icon: 'fas fa-list' },
  // 库存管理
  '/inventory/InventoryList': { title: '库存查询', icon: 'fas fa-warehouse' },
  '/inventory/InventoryCheckList': { title: '库存盘点', icon: 'fas fa-clipboard-list' },
  '/inventory/InventoryCheckAdd': { title: '新增库存盘点', icon: 'fas fa-plus-circle' },
  '/inventory/AssemblyAdd': { title: '新增组装', icon: 'fas fa-tools' },
  '/inventory/AssemblyList': { title: '组装拆装', icon: 'fas fa-tools' },
  '/inventory/OtherTransferAdd': { title: '新增其他出入库', icon: 'fas fa-exchange-alt' },
  '/inventory/OtherTransferList': { title: '其他出入库', icon: 'fas fa-exchange-alt' },
  // 兼容性标题映射
  '/inventory/OtherInboundAdd': { title: '新增其他出入库', icon: 'fas fa-exchange-alt' },
  '/inventory/OtherInboundList': { title: '其他出入库', icon: 'fas fa-exchange-alt' },
  '/inventory/OtherOutboundAdd': { title: '新增其他出入库', icon: 'fas fa-exchange-alt' },
  '/inventory/OtherOutboundList': { title: '其他出入库', icon: 'fas fa-exchange-alt' },
  '/inventory/in': { title: '入库管理', icon: 'fas fa-arrow-down' },
  '/inventory/out': { title: '出库管理', icon: 'fas fa-arrow-up' },
  // 财务管理
  '/Finance/ReceivableList': { title: '应收账款', icon: 'fas fa-money-bill-wave' },
  '/Finance/ReceiptList': { title: '收款单管理', icon: 'fas fa-receipt' },
  '/Finance/ReceiptAdd': { title: '新增收款单', icon: 'fas fa-plus-circle' },
  '/Finance/PayableList': { title: '应付账款', icon: 'fas fa-credit-card' },
  '/Finance/PaymentList': { title: '付款单管理', icon: 'fas fa-money-bill' },
  '/Finance/PaymentAdd': { title: '新增付款单', icon: 'fas fa-plus-circle' },
  '/Finance/IncomeExpenseList': { title: '收支记录管理', icon: 'fas fa-chart-line' },
  '/Finance/IncomeExpenseAdd': { title: '新增收支记录', icon: 'fas fa-plus-circle' },
  '/Finance/IncomeExpenseTypeList': { title: '收支类型管理', icon: 'fas fa-tags' },
  '/Finance/BankAccountList': { title: '结算账户管理', icon: 'fas fa-university' },
  '/Finance/BankAccountAdd': { title: '新增银行账户', icon: 'fas fa-plus' },
  '/Finance/WriteOffVoucherList': { title: '核销单管理', icon: 'fas fa-file-invoice' },
  '/Finance/WriteOffVoucherCreate': { title: '新增核销单', icon: 'fas fa-plus-circle' },
  '/Finance/WriteOffVoucherEdit': { title: '编辑核销单', icon: 'fas fa-edit' },
  '/Finance/WriteOffVoucherDetail': { title: '核销单详情', icon: 'fas fa-file-alt' },
  // '/Finance/InvoiceApplicationListForSales': { title: '销售开票(销售)', icon: 'fas fa-file-invoice' },
  '/Finance/InvoiceApplicationList': { title: '销售开票', icon: 'fas fa-file-invoice' },
  '/Finance/SalesInvoiceList': { title: '销售发票管理', icon: 'fas fa-file-invoice' },
  '/Finance/InvoiceDetail': { title: '发票详情', icon: 'fas fa-file-alt' },
  '/Finance/InvoiceApplicationDetail': { title: '发票申请详情', icon: 'fas fa-file-alt' },
  '/finance/invoice': { title: '发票管理', icon: 'fas fa-file-invoice-dollar' },
  '/finance/payment': { title: '付款处理', icon: 'fas fa-credit-card' },
  '/finance/fund-flows': { title: '资金流水', icon: 'fas fa-exchange-alt' },

  // 系统设置
  '/system/config': { title: '系统配置管理', icon: 'fas fa-cogs' },
  '/settings/UserManagement': { title: '用户管理', icon: 'fas fa-users-cog' }, 
  '/settings/ChannelLevelConfigManagement': { title: '渠道等级配置', icon: 'fas fa-layer-group' },
  '/settings/ChannelMerchantManagement': { title: '渠道商管理', icon: 'fas fa-user-tag' },
  '/settings/salesman': { title: '业务员管理', icon: 'fas fa-user-tie' },
  '/settings/salesman/add': { title: '新增业务员', icon: 'fas fa-user-plus' },
  '/settings/salesman/edit': { title: '编辑业务员', icon: 'fas fa-user-edit' },
  '/settings/roles': { title: '角色权限', icon: 'fas fa-user-shield' },
  '/settings/system': { title: '系统参数', icon: 'fas fa-cogs' },
  '/settings/message-config': { title: '消息配置', icon: 'fas fa-envelope-open-text' },
  '/settings/ProductCreate': { title: '新增商品', icon: 'fas fa-plus-square' },
  '/settings/warehouse': { title: '仓库管理', icon: 'fas fa-warehouse' },
  //报表
  '/Reports/SalesReport': { title: '销售报表', icon: 'fas fa-chart-line' },
  '/reports/employee-commission':{ title: '员工提成报表', icon: 'fas fa-chart-line' },
  '/settings/data-authorization': { title: '数据授权', icon: 'fas fa-lock' },
  '/platform/companies': { title: '企业管理', icon: 'fas fa-building' },
  '/settings/ProductManagement': { title: '商品管理', icon: 'fas fa-boxes' },
  '/settings/CategoryManagement': { title: '商品分类管理', icon: 'fas fa-tags' },
  '/settings/approval-workflows': { title: '审批流程配置', icon: 'fas fa-sitemap' },
  '/Reports/CompanyFinanceReport': { title: '公司财务报表', icon: 'fas fa-chart-line' },
  '/Reports/CustomerReport': { title: '客户报表', icon: 'fas fa-chart-line' },
  // 审批管理
  '/approval/my-pending': { title: '我的待办', icon: 'fas fa-clock' },
  '/approval/my-completed': { title: '我的已办', icon: 'fas fa-check-circle' },
  '/approval/my-applications': { title: '我的申请', icon: 'fas fa-file-alt' },
  '/approval/payment-management': { title: '付款申请管理', icon: 'fas fa-money-bill-wave' },
  '/approval-v2/payment': { title: '付款申请管理', icon: 'fas fa-money-bill-wave' },
  '/approval-v2/payment/add': { title: '新建付款申请', icon: 'fas fa-plus' },
  '/approval-v2/payment/edit':{ title:'修改付款申请', icon: 'fas fa-edit' },
  '/approval/invoice-management': { title: '发票申请管理', icon: 'fas fa-file-invoice' },
  '/approval-v2/invoice': { title: '发票申请管理', icon: 'fas fa-file-invoice' },
  '/approval-v2/invoice/add': { title: '新建发票申请', icon: 'fas fa-plus' },
  '/approval-v2/invoice/edit':{ title:'修改发票申请', icon: 'fas fa-edit' },
  '/approval/seal-management': { title: '印章申请管理', icon: 'fas fa-stamp' },
  '/approval-v2/seal/add': { title: '新建印章申请', icon: 'fas fa-plus' },
  '/approval-v2/seal/edit':{ title:'修改印章申请', icon: 'fas fa-edit' },
  '/approval/history': { title: '审批历史', icon: 'fas fa-history' },
  '/approval/email-action': { title: '邮件审批', icon: 'fas fa-envelope' },
  '/approval/email-template-config': { title: '邮件模板配置', icon: 'fas fa-envelope-open-text' },

  '/ShopOrders/OrderList': { title: '订单管理', icon: 'fas fa-shopping-cart' },
}

// 获取路由标题和图标
const getRouteInfo = (path) => {
  return routeTitleMap[path] || { title: '未知页面', icon: 'fas fa-file' }
}

// 设置标签页元素引用
const setTabRef = (el, path) => {
  if (el) {
    tabRefs.value[path] = el
  } else {
    delete tabRefs.value[path]
  }
}

// 检查是否需要显示翻页按钮
const checkScrollButtons = () => {
  if (!tabsContainer.value) return
  
  const container = tabsContainer.value
  const scrollLeft = container.scrollLeft
  const scrollWidth = container.scrollWidth
  const clientWidth = container.clientWidth
  
  showLeftArrow.value = scrollLeft > 0
  showRightArrow.value = scrollLeft < scrollWidth - clientWidth
}

// 处理滚动事件
const handleScroll = () => {
  checkScrollButtons()
}

// 向左滚动
const scrollLeft = () => {
  if (!tabsContainer.value) return
  tabsContainer.value.scrollBy({ left: -CONSTANTS.SCROLL_AMOUNT, behavior: 'smooth' })
}

// 向右滚动
const scrollRight = () => {
  if (!tabsContainer.value) return
  tabsContainer.value.scrollBy({ left: CONSTANTS.SCROLL_AMOUNT, behavior: 'smooth' })
}

// 滚动到指定标签页
const scrollToTab = (tabPath) => {
  if (!tabsContainer.value || !tabRefs.value[tabPath]) return
  
  const tabElement = tabRefs.value[tabPath]
  const container = tabsContainer.value
  const containerRect = container.getBoundingClientRect()
  const tabRect = tabElement.getBoundingClientRect()
  
  // 计算标签页相对于容器的位置
  const tabLeft = tabRect.left - containerRect.left + container.scrollLeft
  const tabWidth = tabRect.width
  const containerWidth = containerRect.width
  
  // 如果标签页不在可视区域内，滚动到合适位置
  if (tabLeft < container.scrollLeft) {
    // 标签页在左侧不可见，滚动到左边
    container.scrollTo({ left: tabLeft - 20, behavior: 'smooth' })
  } else if (tabLeft + tabWidth > container.scrollLeft + containerWidth) {
    // 标签页在右侧不可见，滚动到右边
    container.scrollTo({ left: tabLeft + tabWidth - containerWidth + 20, behavior: 'smooth' })
  }
}

// 添加标签页
const addTab = (routeInfo) => {
  const { path, name } = routeInfo
  const existingTab = tabs.value.find(tab => tab.path === path)
  
  if (!existingTab) {
    // 标签页不存在，创建新的标签页
    const { title, icon } = getRouteInfo(path)
    tabs.value.push({
      path,
      name,
      title,
      icon
    })
    // 为新标签页初始化组件key
    if (!componentKeys.value[path]) {
      componentKeys.value[path] = 0
    }
    // 激活新标签页
    activeTabPath.value = path
    // 新添加的标签页，等待DOM更新后滚动到该标签页
    nextTick(() => {
      scrollToTab(path)
      checkScrollButtons()
    })
  } else {
    // 如果标签页已存在，激活该标签页并刷新内容
    activeTabPath.value = path
    // 刷新已存在的标签页内容
    refreshTab(existingTab)
    // 滚动到该标签页
    nextTick(() => {
      scrollToTab(path)
    })
  }
}

// 防抖函数，避免频繁的路由跳转
const debounce = (func, wait) => {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

// 防抖的路由跳转函数
const debouncedRouterPush = debounce((path) => {
  if (route.path !== path) {
    router.push(path).catch(err => {
      // 处理路由跳转错误（如重复导航）
      if (err.name !== 'NavigationDuplicated') {
        console.warn('路由跳转失败:', err)
      }
    })
  }
}, CONSTANTS.DEBOUNCE_DELAY)

// 切换标签页
const switchTab = (tab) => {
  // 更新激活的标签页路径
  activeTabPath.value = tab.path
  
  // 使用防抖的路由跳转
  debouncedRouterPush(tab.path)
  
  // 切换标签页时滚动到该标签页
  nextTick(() => {
    scrollToTab(tab.path)
  })
}

// 关闭标签页
const closeTab = (tabToClose) => {
  const index = tabs.value.findIndex(tab => tab.path === tabToClose.path)
  if (index === -1) return
  
  // 如果关闭的是当前标签页，需要切换到其他标签页
  if (tabToClose.path === activeTabPath.value && tabs.value.length > 1) {
    // 优先切换到右边的标签页，如果没有则切换到左边的
    const nextTab = tabs.value[index + 1] || tabs.value[index - 1]
    if (nextTab) {
      activeTabPath.value = nextTab.path
      // 同步更新路由
      if (route.path !== nextTab.path) {
        router.push(nextTab.path)
      }
    }
  }

  // 移除标签页
  tabs.value.splice(index, 1)

  // 如果没有标签页了，切换到仪表板
  if (tabs.value.length === 0) {
    activeTabPath.value = CONSTANTS.DEFAULT_ROUTE
    // 同步更新路由
    if (route.path !== CONSTANTS.DEFAULT_ROUTE) {
      router.push(CONSTANTS.DEFAULT_ROUTE)
    }
  }
}

// 关闭其他标签页
const closeOtherTabs = () => {
  const currentTab = tabs.value.find(tab => tab.path === activeTabPath.value)
  if (currentTab) {
    tabs.value = [currentTab]
  }
}

// 关闭所有标签页
const closeAllTabs = () => {
  tabs.value = []
  activeTabPath.value = CONSTANTS.DEFAULT_ROUTE
  // 同步更新路由
  if (route.path !== CONSTANTS.DEFAULT_ROUTE) {
    router.push(CONSTANTS.DEFAULT_ROUTE)
  }
}

// 组件刷新的key，用于强制重新渲染
const componentKeys = ref({})

// 获取组件的key
const getComponentKey = (path) => {
  if (!componentKeys.value[path]) {
    componentKeys.value[path] = 0
  }
  return componentKeys.value[path]
}

// 刷新标签页
const refreshTab = (tab) => {
  // 通过改变key来强制重新渲染组件
  if (!componentKeys.value[tab.path]) {
    componentKeys.value[tab.path] = 0
  }
  componentKeys.value[tab.path]++
  
  // 如果不是当前激活的标签页，先激活它
  if (activeTabPath.value !== tab.path) {
    activeTabPath.value = tab.path
    // 滚动到该标签页
    nextTick(() => {
      scrollToTab(tab.path)
    })
  }
}

// 定义不需要在选项卡中打开的页面路径
const excludeFromTabs = [
  '/login',
  '/Sales/SalesReportMobile'  // 移动端销售报表页面
]

// 检查路径是否应该排除在选项卡之外
const shouldExcludeFromTabs = (path) => {
  return excludeFromTabs.includes(path)
}

// 监听路由变化
watch(
  () => route.path,
  (newPath) => {
    // 跳过不需要在选项卡中打开的页面
    if (shouldExcludeFromTabs(newPath)) return
    
    // 如果是根路径，等待重定向到dashboard
    if (newPath === '/') return
    
    // 确保dashboard标签页被创建
    if (newPath === CONSTANTS.DEFAULT_ROUTE && tabs.value.length === 0) {
      addTab({
        path: newPath,
        name: route.name || 'MainDashboard'
      })
      return
    }
    
    // 只有当新路径与当前激活的标签页路径不同时，才添加/激活标签页
    if (newPath !== activeTabPath.value) {
      addTab({
        path: newPath,
        name: route.name
      })
    }
  },
  { immediate: true }
)

// 监听标签页数量变化，更新滚动按钮状态
watch(
  () => tabs.value.length,
  () => {
    nextTick(() => {
      checkScrollButtons()
    })
  }
)

// 窗口大小变化处理
const handleResize = () => {
  checkScrollButtons()
}

// 组件挂载时添加事件监听
onMounted(() => {
  window.addEventListener('resize', handleResize)
  nextTick(() => {
    checkScrollButtons()
    
    // 确保初始化时有dashboard标签页
    if (route.path === CONSTANTS.DEFAULT_ROUTE && tabs.value.length === 0) {
      addTab({
        path: CONSTANTS.DEFAULT_ROUTE,
        name: route.name || 'MainDashboard'
      })
    }
  })
})

// 组件卸载时移除事件监听
onUnmounted(() => {
  window.removeEventListener('resize', handleResize)
})


</script>

<style scoped>
/* 隐藏滚动条但保持滚动功能 */
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.scrollbar-hide::-webkit-scrollbar {
  display: none;
}

/* 平滑滚动 */
.scroll-smooth {
  scroll-behavior: smooth;
}

/* 标签页过渡动画 */
.tab-manager {
  transition: all 0.2s ease;
}

/* 标签页悬停效果 */
.group:hover .opacity-0 {
  opacity: 1;
}

/* 翻页按钮过渡效果 */
.tab-manager button {
  transition: all 0.2s ease;
}

/* 确保标签页不会被压缩 */
.flex-shrink-0 {
  flex-shrink: 0;
}
</style>
